import { invoke } from '@tauri-apps/api/core';
import type { OpenedStore } from './open';
import { saveFile } from '../lib/export';

const stamp = () => new Date().toISOString().slice(0, 10);
const FILTER = { name: 'Nexus Open', extensions: ['nexus'] };

/** Where the desktop database lives (Rust `DataDirInfo`). */
export interface DataDirInfo {
  dir: string;
  defaultDir: string;
  custom: boolean;
  fixed: boolean;
  synced: boolean;
}

export const dataDirInfo = () => invoke<DataDirInfo>('data_dir_info');

async function relaunch(): Promise<void> {
  const { relaunch } = await import('@tauri-apps/plugin-process');
  await relaunch();
}

/** Pick a folder (desktop). */
export async function pickFolder(): Promise<string | null> {
  const { open } = await import('@tauri-apps/plugin-dialog');
  const dir = await open({ directory: true, multiple: false });
  return typeof dir === 'string' ? dir : null;
}

/**
 * Use another data folder: "move" takes the current data along, "open" uses what
 * is there. `null` goes back to the default folder. The app restarts.
 */
export async function setDataDir(dir: string | null, mode: 'move' | 'open'): Promise<void> {
  await invoke('data_dir_set', { dir, mode });
  await relaunch();
}

/** The whole workspace, attachments included, as one `.nexus` file. */
export async function makeBackup(opened: OpenedStore): Promise<boolean> {
  const name = `Nexus-Open-${stamp()}.nexus`;
  if (opened.backend === 'desktop') {
    const { save } = await import('@tauri-apps/plugin-dialog');
    const path = await save({ defaultPath: name, filters: [FILTER] });
    if (!path) return false;
    await invoke('db_backup', { path });
    return true;
  }
  const bytes = await opened.driver.exportDb!();
  return saveFile(name, bytes, 'application/vnd.sqlite3', FILTER);
}

/** Pick a `.nexus` file (desktop: its path, browser: its bytes). */
export async function pickBackup(opened: OpenedStore): Promise<string | Uint8Array | null> {
  if (opened.backend === 'desktop') {
    const { open } = await import('@tauri-apps/plugin-dialog');
    const path = await open({ multiple: false, filters: [FILTER] });
    return typeof path === 'string' ? path : null;
  }
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.nexus,.sqlite3,.sqlite,.db';
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return resolve(null);
      void file.arrayBuffer().then((b) => resolve(new Uint8Array(b)));
    };
    input.addEventListener('cancel', () => resolve(null));
    input.click();
  });
}

/** Replace everything on this device with the backup, then start again. */
export async function restoreBackup(
  opened: OpenedStore,
  source: string | Uint8Array,
): Promise<void> {
  if (typeof source === 'string') {
    await invoke('db_restore', { path: source });
    await relaunch();
    return;
  }
  await opened.driver.importDb!(source);
  window.location.reload();
}

/** Open the data folder although another computer holds its lock. */
export async function openAnyway(): Promise<void> {
  await invoke('db_open', { force: true });
  window.location.reload();
}

/** `locked:<computer>` from the desktop's `db_open`. */
export function lockedBy(error: string): string | null {
  return error.startsWith('locked:') ? error.slice('locked:'.length) : null;
}
